import React, { useEffect, useState, useCallback } from "react"
import { Card, CardBody, Button } from "reactstrap"
import { useNavigate, useLocation } from "react-router-dom"

import { toast } from "react-toastify"

import GradeHeader from "../components/GradeHeader"
import GradeGrid from "../components/GradeGrid"
import { getStartOfWeek, addDays, toISODate } from "../../../utils/date"

import { ContentLoader, OverlayLoader } from "../../../components/Common"

import SessionService from "../../../services/session_service"
import EnrollmentService from "../../../services/enrollment_service"

// import { automationService } from "../../../services/Automation/AutomationService"
import { useTenant } from "../../../hooks/useTenant"

import "./EnrollmentGrade.scss"

/**
 * Página de Seleção de Turma/Sessão para Matrícula ou Aula Experimental.
 * Consome o GradeContext para manter a data e filtros sincronizados com a Grade Principal.
 */
const EnrollmentGrade = ({ 
    isEmbed = false, 
    embedMode = 'regular', 
    onConfirmEmbed = null, 
    onCancelEmbed = null,
    preSelectedClasses = [] 
}) => {

    const navigate = useNavigate()
    const location = useLocation()
    const { idTenant, idBranch, user } = useTenant()

    // 1. Estados Locais de Grade (Substituindo GradeContext)
    const [sessions, setSessions] = useState([])
    const [sessionsCache, setSessionsCache] = useState({})
    const [loadingData, setLoadingData] = useState(false)
    const [referenceDate, setReferenceDate] = useState(new Date())
    const [view, setView] = useState('week')
    const [turn, setTurn] = useState('all')

    // String memoizada para evitar o loop infinito no useCallback e useEffect
    // pois objetos Date seriam recriados e falhariam a equality de dependência
    const weekStartISO = view === 'week' ? toISODate(getStartOfWeek(referenceDate)) : toISODate(referenceDate);
    const weekEndISO = view === 'week' ? toISODate(addDays(getStartOfWeek(referenceDate), 6)) : toISODate(referenceDate);

    const fetchSessions = useCallback(async (forceRefresh = false) => {
        if (!idTenant) return;

        const cacheKey = `${weekStartISO}_${weekEndISO}_${view}`;
        if (!forceRefresh && sessionsCache[cacheKey]) {
            let filtered = sessionsCache[cacheKey];
            if (turn !== 'all') {
                const isMorning = turn === 'morning';
                const isAfternoon = turn === 'afternoon';
                const isEvening = turn === 'evening';
                filtered = filtered.filter(s => {
                    const hour = parseInt((s.startTime || "00:00").split(":")[0], 10);
                    if (isMorning) return hour < 12;
                    if (isAfternoon) return hour >= 12 && hour < 18;
                    if (isEvening) return hour >= 18;
                    return true;
                });
            }
            setSessions(filtered);
            return;
        }

        setLoadingData(true);
        try {
            const data = await SessionService.getSessions(idTenant, weekStartISO, weekEndISO);

            let filtered = data;
            if (turn !== 'all') {
                const isMorning = turn === 'morning';
                const isAfternoon = turn === 'afternoon';
                const isEvening = turn === 'evening';
                filtered = data.filter(s => {
                    const hour = parseInt((s.startTime || "00:00").split(":")[0], 10);
                    if (isMorning) return hour < 12;
                    if (isAfternoon) return hour >= 12 && hour < 18;
                    if (isEvening) return hour >= 18;
                    return true;
                });
            }
            setSessions(filtered);
            setSessionsCache(prev => ({ ...prev, [cacheKey]: data }));
        } catch (error) {
            console.error("Erro ao buscar sessoes:", error);
        } finally {
            setLoadingData(false);
        }
    }, [idTenant, weekStartISO, weekEndISO, turn, view, sessionsCache]);

    const refresh = () => fetchSessions(true);

    useEffect(() => {
        if (idTenant) {
            fetchSessions();
        }
    }, [idTenant, fetchSessions]);

    // 2. Parâmetros da URL e Resolução de Contexto
    const searchParams = new URLSearchParams(location.search)
    const idMember = searchParams.get('idMember') || '' // PDV não tem idMember real até fechar a venda
    const memberName = searchParams.get('memberName') || ''
    const mode = isEmbed ? embedMode : searchParams.get('mode') // 'regular' ou 'trial'
    const idMembership = searchParams.get('idMembership') // Só via URL

    // 3. Estados Locais de Seleção
    const [selectedClasses, setSelectedClasses] = useState(preSelectedClasses || []) // Para mode=regular
    const [selectedSession, setSelectedSession] = useState(null) // Para mode=trial
    const [enrolling, setEnrolling] = useState(false)
    const [existingEnrollments, setExistingEnrollments] = useState([]) // Matrículas já existentes

    // Buscar matrículas existentes do cliente para marcar na grade
    useEffect(() => {
        const fetchExistingEnrollments = async () => {
            if (isEmbed) return // Se for modal de carrinho, ele provavelmente ainda não tem idMember
            if (!idMember || !idTenant || !idBranch) return

            try {
                const enrollments = await EnrollmentService.listMemberEnrollments(idTenant, idMember)
                const activeEnrollments = enrollments.filter(e => e.status === 'active')
                setExistingEnrollments(activeEnrollments)

            } catch (error) {
                console.error('Erro ao buscar matrículas existentes:', error)
            }
        }

        fetchExistingEnrollments()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idMember, idTenant, idBranch])



    // Handler de seleção
    const handleSelectSchedule = (schedule) => {
        if (mode === 'trial') {
            // Modo experimental: seleciona apenas UMA sessão
            setSelectedSession(schedule.id === selectedSession ? null : schedule.id)
        } else {
            // Modo regular: seleciona a TURMA (idClass)
            const classId = schedule.idClass
            const sessionDate = schedule.sessionDate || schedule.dateString || toISODate(referenceDate)

            if (selectedClasses.some(c => c.idClass === classId)) {
                setSelectedClasses(prev => prev.filter(c => c.idClass !== classId))
            } else {
                setSelectedClasses(prev => [...prev, { 
                    idClass: classId, 
                    startDate: sessionDate,
                    activityName: schedule.activityName || schedule.className || 'Atividade',
                    teacherName: schedule.employeeName || schedule.teacherName || 'Professor',
                    startTime: schedule.startTime || schedule.time || '--:--',
                    dayOfWeek: schedule.weekDayName || schedule.dayOfWeekStr || 'Dia'
                }])
            }
        }
    }

    // Confirmar matrícula/experimental
    const handleConfirm = async () => {
        // Modo Embed (Passando o payload para a tela superior sem salvar no BD)
        if (isEmbed) {
            if (mode === 'trial' && !selectedSession) {
                toast.error('Selecione uma sessão'); return;
            }
            if (mode === 'regular' && selectedClasses.length === 0) {
                toast.error('Selecione ao menos uma turma'); return;
            }
            if (onConfirmEmbed) {
                onConfirmEmbed(mode === 'trial' ? [selectedSession] : selectedClasses);
            }
            return;
        }

        if (!idMember) {
            toast.error('Membro não identificado')
            return
        }

        if (mode === 'trial' && !selectedSession) {
            toast.error('Selecione uma sessão para a aula experimental')
            return
        }

        if (mode === 'regular' && selectedClasses.length === 0) {
            toast.error('Selecione ao menos uma turma para matrícula')
            return
        }

        try {
            setEnrolling(true)

            if (mode === 'trial') {
                // Agendar experimental
                await EnrollmentService.scheduleTrialClass(idTenant, user, {
                    idMember,
                    sessionId: selectedSession,
                    memberName
                })

                // Automação feita via Firestore Trigger (onTrialScheduled)

                toast.success('Aula experimental agendada com sucesso!')
            } else {
                // Matrícula regular em múltiplas turmas
                // Enviando selectedClasses que contém idClass e startDate específico por turma clicada
                await EnrollmentService.enrollMember(idTenant, user, {
                    idMember,
                    idMembership,
                    selectedClasses, // Array de { idClass, startDate }
                    memberName: memberName || 'Cliente'
                })
                toast.success(`${memberName || 'Cliente'} matriculado(a) com sucesso!`)
            }

            // Atualizar cache da grade forçando atualização antes de sair
            await fetchSessions(true)

            // Voltar para a página anterior (perfil do membro)
            navigate(-1)

        } catch (error) {
            console.error('Erro ao confirmar:', error)
            toast.error(error.message || 'Erro ao processar solicitação')
        } finally {
            setEnrolling(false)
        }
    }

    const handleCancel = () => {
        if (isEmbed && onCancelEmbed) {
            onCancelEmbed();
        } else {
            navigate(-1);
        }
    }

    const isClassEnrolled = (schedule) => {
        return existingEnrollments.some(e => e.idClass === schedule.idClass)
    }

    const isClassSelected = (schedule) => {
        if (mode === 'trial') {
            return schedule.id === selectedSession
        } else {
            return selectedClasses.some(c => c.idClass === schedule.idClass)
        }
    }

    if (loadingData && sessions.length === 0) {
        return <ContentLoader loading={true} message="Carregando sessões..." />
    }

    // Calcula a data de inicio da view para os Data Headers e para o Header Datepicker (pode derivar da memoization ISO também, ou simplesmente ser recomputada na renderizacao)
    const weekStartObj = view === 'week' ? getStartOfWeek(referenceDate) : referenceDate;

    return (
        <React.Fragment>
            {/* Controles da Grade */}
            <Card className="mb-3">
                <CardBody className="pb-2 d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                        <GradeHeader
                            referenceDate={referenceDate}
                            onReferenceDateChange={setReferenceDate}
                            view={view}
                            onViewChange={setView}
                            turn={turn}
                            onTurnChange={setTurn}
                            refresh={refresh}
                            isEmbed={isEmbed}
                            onCancelEmbed={handleCancel}
                        />
                    </div>
                    <div className="d-flex gap-2 ms-4">
                        <Button color="secondary" onClick={handleCancel} disabled={enrolling}>
                            <i className="mdi mdi-close me-1"></i> Cancelar
                        </Button>
                        <Button
                            color={mode === 'trial' ? 'warning' : 'success'}
                            onClick={handleConfirm}
                            disabled={enrolling || (mode === 'trial' ? !selectedSession : selectedClasses.length === 0)}
                            className="px-4"
                        >
                            {enrolling ? "Processando..." : (
                                <><i className={`mdi mdi-${mode === 'trial' ? 'star' : 'check'} me-1`}></i>
                                    {mode === 'trial' ? 'Agendar Experimental' : 'Confirmar Matrícula'}</>
                            )}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* Grade de Seleção */}
            <Card className="position-relative">
                <CardBody>
                    <OverlayLoader show={enrolling} label="Processando matrícula..." />
                    {!loadingData && sessions.length === 0 && (
                        <div className="alert alert-warning mb-3">
                            <i className="mdi mdi-alert me-2"></i>
                            <strong>Nenhuma sessão encontrada.</strong>
                            <p className="mb-0 mt-2">Não há sessões disponíveis para seleção neste período.</p>
                        </div>
                    )}
                    <GradeGrid
                        turn={turn}
                        view={view}
                        referenceDate={referenceDate}
                        weekStart={weekStartObj}
                        schedules={sessions}
                        loading={loadingData}
                        onSelectSchedule={handleSelectSchedule}
                        selectedScheduleId={null}
                        selectedScheduleKey={null}
                        mode="selection"
                        selectedClasses={selectedClasses}
                        selectedSession={selectedSession}
                        isClassSelected={isClassSelected}
                        isClassEnrolled={isClassEnrolled}
                    />
                </CardBody>
            </Card>
        </React.Fragment>
    )
}

export default EnrollmentGrade